import { useRef, useState } from "preact/hooks"
import { toast } from "../../../../components/ui/use-toast"
import { api_fetch } from "../../../../api/client"
import {
  CalendarInfo,
  PagedPromotionsResponse,
  Promotion,
  PromotionLine,
  PromotionPeriodAllocation,
  PromotionStage,
} from "../types"

/** Page size aligned with TPM progressive loading (calendar + list hydrate by calendar). */
const PROMOTION_PAGE_SIZE = 40

export interface PromotionFile {
  id: string
  name: string
  s3_uri: string
  content_type: string
  created_at: string
  actor_name?: string
  promotion_id?: string
}

export interface CopyForwardOptions {
  source_year?: number
  target_year: number
  calendar_id?: string
  customer?: string
  retailer?: string
  planning_group?: string
  product?: string
  promotion_ids?: string[]
  copy_files?: boolean
}

export interface CalendarPromotionLoadState {
  loading_initial: boolean
  loading_more: boolean
  has_more: boolean
  next_offset: number
  error: string | null
}

interface PromotionCreatedResponse {
  id: string
  promotion: Promotion
}

/** True while the calendar list or any calendar's promotion pages are still loading. */
export function any_promotions_loading(
  is_loading_calendars: boolean,
  promotion_load_by_calendar: Record<string, CalendarPromotionLoadState>,
): boolean {
  return (
    is_loading_calendars ||
    Object.values(promotion_load_by_calendar).some(state => state.loading_initial || state.loading_more)
  )
}

export function normalized_next_promotion_offset(
  body: Pick<PagedPromotionsResponse, "promotions" | "next_offset">,
  fallback_offset: number,
): number {
  const next_offset = body.next_offset
  return typeof next_offset === "number" && !Number.isNaN(next_offset) ? next_offset : fallback_offset + body.promotions.length
}

/**
 * Whether a calendar can contribute a promotion to the given year's calendar view.
 *
 * The calendar's own `calendar_year` is only the label it was created with — promotions filed
 * against it are free to run in a different year, and the grid renders them by their own dates.
 * Hydrating on the label alone hid every such promotion: a new account whose only calendar is
 * "Hy-Vee 2027" never loaded its 2026 promotions, so the 2026 view looked empty and the deals
 * were re-entered by hand.
 *
 * Kept permissive on purpose — an unhydrated calendar shows nothing, while an extra one merely
 * costs a request. A calendar with no promotions yet still matches its own label so a freshly
 * created one is not skipped.
 */
export function calendar_holds_year(
  calendar_row: Pick<CalendarInfo, "calendar_year" | "promotion_years" | "has_undated_promotions">,
  year: number,
): boolean {
  if (calendar_row.has_undated_promotions) return true
  if (calendar_row.promotion_years?.includes(year)) return true
  return calendar_row.calendar_year == null || calendar_row.calendar_year === year
}

export function usePromotionalContextAPI() {
  const [calendars, set_calendars] = useState<CalendarInfo[]>([])
  const [promotion_load_by_calendar, set_promotion_load_by_calendar] = useState<Record<string, CalendarPromotionLoadState>>(
    {},
  )
  const [is_loading_calendars, set_is_loading_calendars] = useState(false)
  const [calendars_load_error, set_calendars_load_error] = useState<string | null>(null)
  const [isUploading, setIsUploading] = useState(false)
  const [isProvidingFeedback, setIsProvidingFeedback] = useState(false)
  const [isAddingPromotion, setIsAddingPromotion] = useState(false)
  const [isCreatingCalendar, setIsCreatingCalendar] = useState(false)
  const [isUpdatingCalendar, setIsUpdatingCalendar] = useState(false)

  const hydrate_generation_ref = useRef(0)
  // Calendars whose promotion hydration has already been kicked off for the
  // current generation, so we never double-hydrate. Reset on every fetchCalendars.
  const started_calendars_ref = useRef<Set<string>>(new Set())
  // Mirror of the latest calendars so the on-demand hydration helpers always see
  // current metadata without being recreated as effect dependencies.
  const calendars_ref = useRef<CalendarInfo[]>([])
  calendars_ref.current = calendars

  async function hydrate_calendar_promotion_pages_sequential(calendar_id: string, generation: number): Promise<void> {
    let offset = 0

    while (true) {
      if (hydrate_generation_ref.current !== generation) return

      const is_first_page = offset === 0
      set_promotion_load_by_calendar((previous) => ({
        ...previous,
        [calendar_id]: {
          ...(previous[calendar_id] ?? {
            loading_initial: false,
            loading_more: false,
            has_more: true,
            next_offset: 0,
            error: null,
          }),
          loading_initial: is_first_page,
          loading_more: !is_first_page,
          error: null,
        },
      }))

      const res = await api_fetch<PagedPromotionsResponse>(`/promotional-calendars/${calendar_id}/promotions`, {
        params: {
          limit: PROMOTION_PAGE_SIZE,
          offset,
          sort: "start_date_asc",
        },
      })

      if (hydrate_generation_ref.current !== generation) return

      if (!res.ok) {
        set_promotion_load_by_calendar((previous) => ({
          ...previous,
          [calendar_id]: {
            ...(previous[calendar_id] ?? {
              loading_initial: false,
              loading_more: false,
              has_more: false,
              next_offset: 0,
              error: null,
            }),
            loading_initial: false,
            loading_more: false,
            error: res.error.message,
          },
        }))
        return
      }

      const body = res.value.data
      set_calendars((previous) =>
        previous.map((calendar_row) => {
          if (calendar_row.id !== calendar_id) return calendar_row
          if (is_first_page) {
            return { ...calendar_row, promotions: body.promotions }
          }
          return { ...calendar_row, promotions: [...calendar_row.promotions, ...body.promotions] }
        }),
      )

      const has_more = body.has_more
      const next_offset_value = normalized_next_promotion_offset(body, offset)

      set_promotion_load_by_calendar((previous) => ({
        ...previous,
        [calendar_id]: {
          loading_initial: false,
          loading_more: false,
          has_more,
          next_offset: next_offset_value,
          error: null,
        },
      }))

      if (!has_more) return
      offset = next_offset_value
    }
  }

  async function fetch_calendars_metadata_and_hydrate() {
    // Bump the generation so any promotion hydration still in flight from a
    // previous fetch bails out instead of writing stale data.
    hydrate_generation_ref.current += 1

    set_is_loading_calendars(true)
    set_calendars_load_error(null)

    const res = await api_fetch<CalendarInfo[]>("/promotional-calendars", {
      params: { include_promotions: false },
    })

    if (!res.ok) {
      set_calendars([])
      set_promotion_load_by_calendar({})
      set_is_loading_calendars(false)
      set_calendars_load_error(res.error.message)
      return res
    }

    const metadata = res.value.data.map((calendar_row): CalendarInfo => ({ ...calendar_row, promotions: [] }))

    // Start every calendar in an idle (not-loading) state. Promotion hydration is
    // now triggered on demand per view/year (see hydrate_calendars_for_year /
    // hydrate_all_calendars) instead of fanning out across all calendars on mount,
    // which previously fired one slow request per calendar across every year.
    const initial_promotion_states: Record<string, CalendarPromotionLoadState> = {}
    for (const calendar_row of metadata) {
      initial_promotion_states[calendar_row.id] = {
        loading_initial: false,
        loading_more: false,
        has_more: true,
        next_offset: 0,
        error: null,
      }
    }

    started_calendars_ref.current = new Set()
    calendars_ref.current = metadata
    set_calendars(metadata)
    set_promotion_load_by_calendar(metadata.length === 0 ? {} : initial_promotion_states)
    set_is_loading_calendars(false)

    return res
  }

  /**
   * Kick off progressive promotion hydration for the given calendars, skipping any
   * already started this generation. In-memory bookkeeping only — the actual paged
   * fetches run in the background.
   */
  function start_hydration_for(calendar_ids: string[]) {
    const generation = hydrate_generation_ref.current
    const to_start = calendar_ids.filter((id) => !started_calendars_ref.current.has(id))
    if (to_start.length === 0) return

    for (const id of to_start) started_calendars_ref.current.add(id)

    set_promotion_load_by_calendar((previous) => {
      const next = { ...previous }
      for (const id of to_start) {
        next[id] = { loading_initial: true, loading_more: false, has_more: true, next_offset: 0, error: null }
      }
      return next
    })

    void Promise.all(to_start.map((id) => hydrate_calendar_promotion_pages_sequential(id, generation))).catch(
      (event: unknown) => {
        console.error("Progressive TPM promotion hydration:", event)
      },
    )
  }

  /** Hydrate only the calendars that can show something in the given year (year-scoped calendar view). */
  function hydrate_calendars_for_year(year: number) {
    const ids = calendars_ref.current
      .filter((calendar_row) => calendar_holds_year(calendar_row, year))
      .map((calendar_row) => calendar_row.id)
    start_hydration_for(ids)
  }

  /** Hydrate every calendar (used by the List and Board views, which span all years). */
  function hydrate_all_calendars() {
    start_hydration_for(calendars_ref.current.map((calendar_row) => calendar_row.id))
  }

  async function fetchCalendars() {
    return fetch_calendars_metadata_and_hydrate()
  }

  async function handleFileUpload(files: File[]) {
    if (files.length === 0) return

    setIsUploading(true)
    try {
      const formData = new FormData()
      for (const file of files) {
        formData.append("files", file)
      }

      const response = await fetch("/api/promotional-calendars/upload", {
        method: "POST",
        credentials: "same-origin",
        body: formData,
      })

      if (!response.ok) {
        const errorText = await response.text()
        let errorMessage = "Upload failed"
        try {
          const errorData = JSON.parse(errorText)
          errorMessage = errorData.message || errorMessage
        } catch (_e) {
          errorMessage = errorText || errorMessage
        }

        toast({
          title: "Upload Failed",
          description: errorMessage,
          variant: "destructive",
        })
        return
      }

      await response.json()
      toast({
        title: "Analysis Complete",
        description:
          "Your promotional calendar has been analyzed and saved. You can now edit individual promotions and provide feedback for re-analysis.",
      })

      // Refresh and open the new calendar
      const calendarRes = await fetchCalendars()
      if (calendarRes.ok && calendarRes.value.data.length > 0) {
        return calendarRes.value.data[0].id // Return the new calendar ID
      }
    } catch (error) {
      console.error("Error uploading files:", error)
      toast({
        title: "Upload Failed",
        description: "Failed to upload files. Please try again.",
        variant: "destructive",
      })
    } finally {
      setIsUploading(false)
    }
  }

  async function handleProvideFeedback(calendar: CalendarInfo, feedback: string) {
    if (!feedback.trim()) return

    // For AI-generated calendars, we need the source_context_id to provide feedback
    if (calendar.source_type === "ai_upload" && !calendar.source_context_id) {
      toast({
        title: "Feedback Failed",
        description: "Cannot provide feedback for this calendar. Missing analysis context.",
        variant: "destructive",
      })
      return false
    }

    setIsProvidingFeedback(true)
    try {
      const res = await api_fetch(`/promotional-calendars/analysis/${calendar.source_context_id}/feedback`, {
        method: "POST",
        body: {
          feedback: feedback,
        },
      })

      if (res.ok) {
        toast({
          title: "Feedback Applied",
          description: "Your feedback has been applied to the analysis.",
        })

        await fetchCalendars()
        return true
      } else {
        toast({
          title: "Feedback Failed",
          description: res.error.message,
          variant: "destructive",
        })
        return false
      }
    } catch (error) {
      console.error("Error providing feedback:", error)
      toast({
        title: "Feedback Failed",
        description: "Failed to apply feedback. Please try again.",
        variant: "destructive",
      })
      return false
    } finally {
      setIsProvidingFeedback(false)
    }
  }

  async function handleCreatePromotion(calendarId: string, promotion: Promotion) {
    setIsAddingPromotion(true)
    try {
      const res = await api_fetch<PromotionCreatedResponse>(`/promotional-calendars/${calendarId}/promotions`, {
        method: "POST",
        body: {
          promotion: promotion,
        },
      })

      if (res.ok) {
        toast({
          title: "Promotion Created",
          description: "New promotion has been added to the calendar.",
        })

        await fetchCalendars()
        return true
      } else {
        toast({
          title: "Creation Failed",
          description: res.error.message,
          variant: "destructive",
        })
        return false
      }
    } catch (error) {
      console.error("Error creating promotion:", error)
      toast({
        title: "Creation Failed",
        description: "Failed to create promotion. Please try again.",
        variant: "destructive",
      })
      return false
    } finally {
      setIsAddingPromotion(false)
    }
  }

  /** Returns the created promotion (with id) on success, or null on failure. */
  async function handleCreatePromotionWithFiles(
    calendarId: string,
    promotion: Promotion,
    files: File[],
  ): Promise<Promotion | null> {
    setIsAddingPromotion(true)
    try {
      const res = await api_fetch<PromotionCreatedResponse>(`/promotional-calendars/${calendarId}/promotions`, {
        method: "POST",
        body: {
          promotion: promotion,
        },
      })

      if (!res.ok) {
        toast({
          title: "Creation Failed",
          description: res.error.message,
          variant: "destructive",
        })
        return null
      }

      toast({
        title: "Promotion Created",
        description: "New promotion has been added to the calendar.",
      })

      const promotion_id = res.value.data.promotion?.id ?? res.value.data.id
      await fetchCalendars()

      if (promotion_id && files.length > 0) {
        await uploadPromotionFiles(promotion_id, files)
        await fetchCalendars()
      }

      const created = res.value.data.promotion ?? promotion
      return { ...created, id: promotion_id }
    } catch (error) {
      console.error("Error creating promotion:", error)
      toast({
        title: "Creation Failed",
        description: "Failed to create promotion. Please try again.",
        variant: "destructive",
      })
      return null
    } finally {
      setIsAddingPromotion(false)
    }
  }

  async function handleUpdatePromotionStage(promotionId: string, stage: PromotionStage, approver_id?: string) {
    const res = await api_fetch(`/promotions/${promotionId}/stage`, {
      method: "PATCH",
      body: { stage, approver_id },
    })

    if (res.ok) {
      await fetchCalendars()
      return true
    }

    toast({
      title: "Stage Update Failed",
      description: res.error.message,
      variant: "destructive",
    })
    return false
  }

  async function handleDuplicatePromotion(
    promotionId: string,
    options: {
      repeat_every_months?: number
      number_of_repetitions?: number
      custom_months?: Array<{ year: number; month: number }>
      copy_files?: boolean
    },
  ) {
    const res = await api_fetch(`/promotions/${promotionId}/duplicate`, {
      method: "POST",
      body: options,
    })

    if (res.ok) {
      toast({
        title: "Promotion Duplicated",
        description: "The duplicated promotions were added to planning.",
      })
      await fetchCalendars()
      return true
    }

    toast({
      title: "Duplication Failed",
      description: res.error.message,
      variant: "destructive",
    })
    return false
  }

  /** Returns the created promotions on success (possibly empty), or null on failure. */
  async function handleCopyForward(options: CopyForwardOptions): Promise<Promotion[] | null> {
    const res = await api_fetch<Promotion[]>(`/promotions/copy-forward`, {
      method: "POST",
      body: options,
    })

    if (res.ok) {
      const copied = res.value.data ?? []
      toast({
        title: "Promotions Copied Forward",
        description:
          copied.length > 0
            ? `${copied.length} promotion${copied.length === 1 ? "" : "s"} copied to ${options.target_year}.`
            : "No promotions matched the selected source year and filters.",
      })
      await fetchCalendars()
      return copied
    }

    toast({
      title: "Copy Forward Failed",
      description: res.error.message,
      variant: "destructive",
    })
    return null
  }

  async function handleSplitFixedFee(promotionId: string, allocations?: PromotionPeriodAllocation[]) {
    const res = await api_fetch(`/promotions/${promotionId}/split-fixed-fee`, {
      method: "POST",
      body: { allocations },
    })

    if (res.ok) {
      toast({
        title: "Fixed Fee Split",
        description: "The planned amount has been split by period.",
      })
      await fetchCalendars()
      return true
    }

    toast({
      title: "Split Failed",
      description: res.error.message,
      variant: "destructive",
    })
    return false
  }

  async function handleUpdatePromotion(promotion: Promotion) {
    if (!promotion.id) {
      toast({
        title: "Update Failed",
        description: "Cannot update promotion without ID",
        variant: "destructive",
      })
      return false
    }

    try {
      const res = await api_fetch(`/promotions/${promotion.id}`, {
        method: "PUT",
        body: {
          promotion: promotion,
        },
      })

      if (res.ok) {
        toast({
          title: "Promotion Updated",
          description: "Promotion has been updated successfully.",
        })

        await fetchCalendars()
        return true
      } else {
        toast({
          title: "Update Failed",
          description: res.error.message,
          variant: "destructive",
        })
        return false
      }
    } catch (error) {
      console.error("Error updating promotion:", error)
      toast({
        title: "Update Failed",
        description: "Failed to update promotion. Please try again.",
        variant: "destructive",
      })
      return false
    }
  }

  async function handleDeletePromotion(promotionId: string) {
    try {
      const res = await api_fetch(`/promotions/${promotionId}`, {
        method: "DELETE",
      })

      if (res.ok) {
        toast({
          title: "Promotion Deleted",
          description: "Promotion has been deleted successfully.",
        })

        await fetchCalendars()
        return true
      } else {
        toast({
          title: "Delete Failed",
          description: res.error.message,
          variant: "destructive",
        })
        return false
      }
    } catch (error) {
      console.error("Error deleting promotion:", error)
      toast({
        title: "Delete Failed",
        description: "Failed to delete promotion. Please try again.",
        variant: "destructive",
      })
      return false
    }
  }

  async function handleCreateCalendar(calendarData: {
    name: string
    description?: string
    year?: number
    retailer_name?: string
    brand_name?: string
    customer_name?: string
  }) {
    setIsCreatingCalendar(true)
    try {
      const res = await api_fetch("/promotional-calendars", {
        method: "POST",
        body: calendarData,
      })

      if (res.ok) {
        toast({
          title: "Calendar Created",
          description: "New promotional calendar has been created successfully.",
        })

        const calendarRes = await fetchCalendars()
        if (calendarRes.ok && calendarRes.value.data.length > 0) {
          return calendarRes.value.data[0].id // Return the new calendar ID
        }
        return true
      } else {
        toast({
          title: "Creation Failed",
          description: res.error.message,
          variant: "destructive",
        })
        return false
      }
    } catch (error) {
      console.error("Error creating calendar:", error)
      toast({
        title: "Creation Failed",
        description: "Failed to create calendar. Please try again.",
        variant: "destructive",
      })
      return false
    } finally {
      setIsCreatingCalendar(false)
    }
  }

  /** Update a calendar's own metadata (name, description, year, retailer, brand) — not its promotions. */
  async function handleUpdateCalendar(
    calendarId: string,
    calendarData: {
      name: string
      description?: string
      year?: number
      retailer_name?: string
      brand_name?: string
      customer_name?: string
    },
  ): Promise<boolean> {
    setIsUpdatingCalendar(true)
    try {
      const res = await api_fetch(`/promotional-calendars/${calendarId}`, {
        method: "PUT",
        body: calendarData,
      })

      if (res.ok) {
        toast({
          title: "Calendar Updated",
          description: "The calendar details have been saved.",
        })
        await fetchCalendars()
        return true
      }

      toast({
        title: "Update Failed",
        description: res.error.message,
        variant: "destructive",
      })
      return false
    } catch (error) {
      console.error("Error updating calendar:", error)
      toast({
        title: "Update Failed",
        description: "Failed to update calendar. Please try again.",
        variant: "destructive",
      })
      return false
    } finally {
      setIsUpdatingCalendar(false)
    }
  }

  async function uploadPromotionFiles(promotionId: string, files: File[]): Promise<PromotionFile[]> {
    if (files.length === 0) return []

    const formData = new FormData()
    for (const file of files) {
      formData.append("files", file)
    }

    try {
      const response = await fetch(`/api/promotions/${promotionId}/files`, {
        method: "POST",
        credentials: "same-origin",
        body: formData,
      })

      if (!response.ok) {
        const errorText = await response.text()
        let errorMessage = "Upload failed"
        try {
          const errorData = JSON.parse(errorText)
          errorMessage = errorData.message || errorMessage
        } catch (_e) {
          errorMessage = errorText || errorMessage
        }

        toast({
          title: "Upload Failed",
          description: errorMessage,
          variant: "destructive",
        })
        return []
      }

      const data = await response.json()
      toast({
        title: "Files Uploaded",
        description: `${files.length} file${files.length !== 1 ? "s" : ""} uploaded successfully.`,
      })
      return data.data || data
    } catch (error) {
      console.error("Error uploading files:", error)
      toast({
        title: "Upload Failed",
        description: "Failed to upload files. Please try again.",
        variant: "destructive",
      })
      return []
    }
  }

  async function listPromotionFiles(promotionId: string): Promise<PromotionFile[]> {
    try {
      const res = await api_fetch<PromotionFile[]>(`/promotions/${promotionId}/files`)
      if (res.ok) {
        return res.value.data || []
      }
      return []
    } catch (error) {
      console.error("Error listing files:", error)
      return []
    }
  }

  async function deletePromotionFile(promotionId: string, s3Uri: string): Promise<boolean> {
    try {
      const encodedUri = encodeURIComponent(s3Uri)
      const res = await api_fetch(`/promotions/${promotionId}/files/${encodedUri}`, {
        method: "DELETE",
      })

      if (res.ok) {
        toast({
          title: "File Deleted",
          description: "File has been deleted successfully.",
        })
        return true
      } else {
        toast({
          title: "Delete Failed",
          description: res.error.message,
          variant: "destructive",
        })
        return false
      }
    } catch (error) {
      console.error("Error deleting file:", error)
      toast({
        title: "Delete Failed",
        description: "Failed to delete file. Please try again.",
        variant: "destructive",
      })
      return false
    }
  }

  async function listPromotionLines(promotionId: string): Promise<PromotionLine[]> {
    try {
      const res = await api_fetch<PromotionLine[]>(`/promotions/${promotionId}/lines`)
      if (res.ok) {
        return res.value.data || []
      }
      return []
    } catch (error) {
      console.error("Error listing promotion lines:", error)
      return []
    }
  }

  /** Fetch a single hydrated promotion by id (used for deep-linking to its detail popup). */
  async function getPromotion(promotionId: string): Promise<Promotion | null> {
    try {
      const res = await api_fetch<{ id: string; promotion: Promotion }>(`/promotions/${promotionId}`)
      if (res.ok) {
        return res.value.data?.promotion ?? null
      }
      return null
    } catch (error) {
      console.error("Error fetching promotion:", error)
      return null
    }
  }

  return {
    calendars,
    promotion_load_by_calendar,
    is_loading_calendars,
    calendars_load_error,
    isUploading,
    isProvidingFeedback,
    isAddingPromotion,
    isCreatingCalendar,
    isUpdatingCalendar,

    fetchCalendars,
    hydrate_calendars_for_year,
    hydrate_all_calendars,
    handleFileUpload,
    handleProvideFeedback,
    handleCreatePromotion,
    handleCreatePromotionWithFiles,
    handleUpdatePromotion,
    handleUpdatePromotionStage,
    handleDeletePromotion,
    handleCreateCalendar,
    handleUpdateCalendar,
    handleDuplicatePromotion,
    handleCopyForward,
    handleSplitFixedFee,
    uploadPromotionFiles,
    listPromotionFiles,
    deletePromotionFile,
    listPromotionLines,
    getPromotion,
  }
}

if (import.meta.vitest) {
  const { describe, expect, it } = import.meta.vitest

  describe("any_promotions_loading", () => {
    const idle = { loading_initial: false, loading_more: false, has_more: false, next_offset: 0, error: null }

    it("is true while the calendar list is loading", () => {
      expect(any_promotions_loading(true, {})).toBe(true)
    })
    it("is true while any calendar's promotion pages are loading", () => {
      expect(any_promotions_loading(false, { a: idle, b: { ...idle, loading_initial: true } })).toBe(true)
      expect(any_promotions_loading(false, { a: { ...idle, loading_more: true } })).toBe(true)
    })
    it("is false once everything settled", () => {
      expect(any_promotions_loading(false, { a: idle })).toBe(false)
      expect(any_promotions_loading(false, {})).toBe(false)
    })
  })

  describe("normalized_next_promotion_offset", () => {
    it("uses next_offset from body when numeric", () => {
      expect(
        normalized_next_promotion_offset({ promotions: [], next_offset: 99 }, 0),
      ).toBe(99)
    })
    it("falls back when next_offset omitted", () => {
      expect(normalized_next_promotion_offset({ promotions: [], next_offset: null }, 10)).toBe(10)
    })
    it("falls back using promotion row count", () => {
      expect(
        normalized_next_promotion_offset(
          {
            promotions: [
              { name: "one", products: [] },
              { name: "two", products: [] },
            ],
            next_offset: undefined,
          },
          40,
        ),
      ).toBe(42)
    })
  })
}
